import { PureComponent } from "react";
import moment, { Moment } from "moment";

import { LoadingSpinner } from "@app/shared";
import { QESButton, QESCheckbox, QESMultiSelect, QESTextInput } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, Waffles, wafflesKeyColumnName } from "@app/shared/waffles";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";

import { GeographyService } from "@app/geography";
import { BasicState } from "@app/geography/state";

import { SoftwareLicenseDetail, SoftwareProduct } from "./clientLicenseModels";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";

interface AddProductPanelProps {
  licenseId: number;
  licensedProducts: SoftwareLicenseDetail[];
  unlicensedProducts: SoftwareProduct[];
  onAddProducts: (productIds: string[], copies: string, isEvaluation: boolean, isNational: boolean, licensedStates: string[], evalExpDate?: Moment) => void;
}

interface AddProductPanelState {
  numCopies: string;
  numCopiesValid: boolean;
  isExpiring: boolean;
  expirationDate?: Moment;
  isNational: boolean;
  selectedProducts: string[];
  licensedStates: string[];
  productTableData: UserTableData[];
  stateList: BasicState[];
  stateOptions: { [key: string]: MultiselectItem };
  loading: boolean;
}

interface UserTableData {
  [wafflesKeyColumnName]: string;
  name: string;
  productId: string;
}

const userColumnConfig: ColumnConfig = {
  name: {
    displayText: "Name",
    testId: "name",
  },
};

class WrappedAddProductPanelContent extends PureComponent<AddProductPanelProps & FlyoutContextOutgoingProps, AddProductPanelState> {
  constructor(props: AddProductPanelProps & FlyoutContextOutgoingProps) {
    super(props);
    this.state = {
      expirationDate: null,
      isExpiring: true,
      isNational: true,
      licensedStates: [],
      loading: true,
      numCopies: "1",
      numCopiesValid: true,
      productTableData: [],
      selectedProducts: [],
      stateList: [],
      stateOptions: {}
    };
  }

  private geographyService = new GeographyService();
  private licensedProductIds = this.props.licensedProducts.filter(x => x.productId > 0).map(x => x.productId.toString());

  public async componentDidMount() {
    if (!this.props.licenseId)
      return;

    const stateList = await this.geographyService.getStatesForDroplist();
    const allLicensedExpirationDate = this.props.licensedProducts.every((val, d, arr) => val.evalExpirationDate === arr[0].evalExpirationDate) ? moment.utc(this.props.licensedProducts[0].evalExpirationDate).startOf("day") : null;
    const allLicensedNumCopies = this.props.licensedProducts.every((val, c, arr) => val.numCopies === arr[0].numCopies) ? this.props.licensedProducts[0].numCopies.toString() : "0";
    const allLicensedNational = this.props.licensedProducts.every((val) => val.licensedStates === undefined);
    const allLicensedStatesMatch = this.props.licensedProducts.every((val, _, arr) => val.licensedStates !== undefined && val.licensedStates === arr[0].licensedStates);
    const allLicensedStatesList = !allLicensedNational && allLicensedStatesMatch ? this.props.licensedProducts[0].licensedStates.split(",").map(s => s.trim()) : [];

    this.setState({
      expirationDate: allLicensedExpirationDate,
      isNational: allLicensedNational,
      licensedStates: allLicensedStatesList,
      loading: false,
      numCopies: allLicensedNumCopies,
      productTableData: this.createProductWafflesTable(this.props.unlicensedProducts),
      selectedProducts: this.licensedProductIds,
      stateList: stateList,
      stateOptions: this.getMultiSelectOptions(stateList, allLicensedStatesList)
    });
  }

  private getMultiSelectOptions(list: BasicState[], selectedItems: string[]) {
    const options: { [key: string]: MultiselectItem } = {};
    list.forEach((o) => Object.assign(options,
      {
        [o.abbreviation]: {
          currentState: selectedItems.find((ls) => ls === o.abbreviation) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: `${o.abbreviation} - ${o.name}`
        }
      }));
    return options;
  }

  private createProductWafflesTable = (data: SoftwareProduct[]) => {
    return data.map((rec) => {
      /* eslint-disable sort-keys */
      const productName = this.licensedProductIds.includes(rec.productId.toString()) ? `${rec.productName} - **LICENSED**` : `${rec.productName}`;
      return {
        key: rec.productId.toString(),
        productId: rec.productId.toString(),
        name: productName
      };
      /* eslint-enable sort-keys */
    });
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    const selectedProducts = this.props.licensedProducts.filter(p => selectedKeys.includes(p.productId.toString()));
    const allSelectedExpirationDate = selectedProducts.length > 0 && selectedProducts.every((val, d, arr) => val.evalExpirationDate === arr[0].evalExpirationDate) ? moment.utc(selectedProducts[0].evalExpirationDate).startOf("day") : null;
    const allSelectedNumCopies = selectedProducts.length > 0 && selectedProducts.every((val, c, arr) => val.numCopies === arr[0].numCopies) ? selectedProducts[0].numCopies.toString() : "0";
    const allSelectedNational = selectedProducts.length > 0 && selectedProducts.every((val) => val.licensedStates === undefined);
    const allSelectedStatesMatch = selectedProducts.length > 0 && selectedProducts.every((val, _, arr) => val.licensedStates !== undefined && val.licensedStates === arr[0].licensedStates);
    const allSelectedStatesList = !allSelectedNational && allSelectedStatesMatch ? selectedProducts[0].licensedStates.split(",").map(s => s.trim()) : [];

    this.setState({
      expirationDate: allSelectedExpirationDate,
      isNational: allSelectedNational,
      licensedStates: allSelectedStatesList,
      numCopies: allSelectedNumCopies,
      selectedProducts: selectedKeys,
      stateOptions: this.getMultiSelectOptions(this.state.stateList, allSelectedStatesList)
    });
  }

  private handleCopiesChange(text: string) {
    const numRegex = /(^\d+$|^$)/g;

    this.setState((prevState) => ({
      numCopies: numRegex.test(text) ? text : prevState.numCopies
    }), () => {
      this.setState({ numCopiesValid: !!this.state.numCopies && parseInt(this.state.numCopies) > 0 });
    });
  }

  private handleEvalExpDateChange(date: string) {
    let expirationDate = moment.utc(date).startOf("day");

    if (expirationDate < moment().startOf("day"))
      expirationDate = moment().startOf("day");

    this.setState({ expirationDate: expirationDate });
  }

  private handleLicensedStateChanged = (response: MultiselectOnChangeResponse) => {
    const selectedStates = response.checkedKeys;
    const newStates = selectedStates.map((state) => {
      const matchedStateModel = this.state.stateList.find((x) => x.abbreviation === state);
      return matchedStateModel;
    });

    const abbrevs = newStates.map(x => x.abbreviation);

    this.setState({
      licensedStates: abbrevs,
      stateOptions: this.getMultiSelectOptions(this.state.stateList, abbrevs)
    });
  }

  private handleSelectAll() {
    const abbrevs = this.state.stateList.map(x => x.abbreviation);

    this.setState({
      licensedStates: abbrevs,
      stateOptions: this.getMultiSelectOptions(this.state.stateList, abbrevs)
    })
  }

  private handleAddClick = () => {
    const evalExpDate = this.state.isExpiring ? this.state.expirationDate : null;
    const licensedStates = this.state.isNational ? [] : this.state.licensedStates;

    this.props.onAddProducts(this.state.selectedProducts, this.state.numCopies, this.state.isExpiring, this.state.isNational, licensedStates, evalExpDate);
    this.props.handleCloseFlyout();
  }

  public render() {
    return (
      <>
        {this.state.loading ? <LoadingSpinner /> :
          <>
            <Waffles<UserTableData>
              columns={userColumnConfig}
              data={this.state.productTableData}
              showCheckboxes={true}
              selectedKeys={this.state.selectedProducts}
              handleCheckboxChange={this.handleCheckboxChange}
              testId="add-product-panel-content__product-grid-waffles-table"
            />
            <QESTextInput
              containerClassName="mt-2 col-2 p-0"
              label="Copies"
              testId="add-product-panel-content__copies"
              required={true}
              isValid={this.state.numCopiesValid}
              value={this.state.numCopies}
              onChange={(e) => this.handleCopiesChange(e.target.value)} />
            <QESDateInput
              containerClassName="client-licenses__date-input col-2 p-0"
              min={moment().format("YYYY-MM-DD")}
              label="Expiration Date"
              testId="add-product-panel-content__eval-exp-date"
              onChange={(e) => this.handleEvalExpDateChange(e.target.value)}
              value={moment(this.state.expirationDate).format("YYYY-MM-DD")}
              required={this.state.isExpiring}
              isValid={this.state.isExpiring && !!this.state.expirationDate}
            />
            <QESCheckbox
              label="National"
              testId="add-product-panel-content__is-national"
              checked={this.state.isNational}
              onChange={(e) => this.setState({ isNational: e.target.checked })} />
            {!this.state.isNational ?
              <>
                <QESMultiSelect
                  containerClassName="col-8 mb-3"
                  errorText="At least one state must be selected."
                  label="Licensed States"
                  onChange={this.handleLicensedStateChanged}
                  options={this.state.stateOptions}
                  required={!this.state.isNational}
                  isValid={!this.state.isNational && this.state.licensedStates.length > 0}
                  testId="add-product-panel-content__licensed-states" />
                <QESButton
                  qesTestId="add-product-panel-content__select-all"
                  qesType="secondary"
                  onClick={() => this.handleSelectAll()}
                >
                  Select All
                </QESButton>
                <QESButton
                  qesTestId="add-product-panel-content__clear-all"
                  qesType="secondary"
                  onClick={() =>
                    this.setState({
                      licensedStates: [],
                      stateOptions: this.getMultiSelectOptions(this.state.stateList, [])
                    })
                  }
                >
                  Clear All
                </QESButton>
              </> : null
            }
            <FlyoutFooter
              submitButton={<QESButton
                qesTestId="add-product-panel-content__confirm"
                className="mr-2"
                qesType="primary"
                onClick={() => { this.handleAddClick(); }}
                disabled={this.state.selectedProducts.length === 0 || !this.state.numCopiesValid || (this.state.isExpiring && !this.state.expirationDate) || (!this.state.isNational && this.state.licensedStates.length === 0)}
              >
                Update
              </QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const AddProductPanelContent = withFlyoutContext(WrappedAddProductPanelContent);

export default AddProductPanelContent;
